/* eslint-disable react-hooks/exhaustive-deps */
import { FullQuestionType } from "@/utils/Types";
import {
  QuestionDetailsAction,
  QuestionVerifyAction,
} from "../../Redux/Actions/QuestionVerifyAction";
import { MenuItem, Modal, Select } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import EmptyData from "../Helper/EmptyData";
import parse from "html-react-parser";
import { Icons } from "../../utils/Icons";
import { capitalizeFirstLetter, toSentenceCase } from "../../utils/Helper";
import SolutionsTabs from "./SolutionsTabs";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { VALIDATION_MESSAGE } from "../../Constants/Constants";
import { ErrorMessage } from "@hookform/error-message";

interface FormInputs {
  question_id: number;
  question_type: string;
  status: string;
  visiblity_status: string;
  remark: string;
}

const formSchema = Yup.object({
  question_id: Yup.number().required(
    VALIDATION_MESSAGE?.ANY_REQUIRED + "Question ID"
  ),
  question_type: Yup.string().required(
    VALIDATION_MESSAGE?.ANY_REQUIRED + "Question Type"
  ),
  status: Yup.string().required("Please select Status"),
  visiblity_status: Yup.string().required("Please select Visibility Level"),
  remark: Yup.string()
    .required(VALIDATION_MESSAGE?.ANY_REQUIRED + "Remark")
    .trim()
    .min(5, "Minimum 5 characters are allowed")
    .max(100, "Maximum 100 characters are allowed"),
});

const QuestionVerificationModal = ({ setIsOpen, modalData }: any) => {
  const dispatch = useDispatch<any>();
  const QuestionDetailsState = useSelector(
    (state: any) => state?.QuestionDetailsState
  );
  const QuestionVerifyState = useSelector(
    (state: any) => state?.QuestionVerifyState
  );

  type ValidationKey = "organization" | "contest" | "public";

  interface VisibilityOption {
    label: string;
    value: string;
    key: ValidationKey;
  }

  const VerifyOptions = [
    { label: "Accept", value: "ACCEPT" },
    { label: "Reject", value: "REJECT" },
    { label: "Review", value: "REVIEW" },
  ];

  const [data, setData] = useState<FullQuestionType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const visibilityOptions: VisibilityOption[] = [
    ...(data?.question_validation?.contest?.length
      ? [
          {
            label: "Contest",
            value: "CONTEST",
            key: "contest" as ValidationKey,
          },
        ]
      : []),
    ...(data?.question_validation?.public?.length
      ? [{ label: "Public", value: "PUBLIC", key: "public" as ValidationKey }]
      : []),
  ];

  const [selectedVisibility, setSelectedVisibility] = useState(
    visibilityOptions[0]?.value ?? ""
  );
  const [selectedVerify, setSelectedVerify] = useState("");
  const [reviewText, setReviewText] = useState<string>("");
  const [lastSelectedStatus, setLastSelectedStatus] = useState<string>("");

  useEffect(() => {
    const entriesForSelected = selectedVisibility
      ? data?.question_validation[
          selectedVisibility?.toLowerCase() as ValidationKey
        ] ?? []
      : [];
    const lastStatusForSelected =
      entriesForSelected?.length > 0
        ? entriesForSelected[entriesForSelected?.length - 1]?.status
        : null;
    setLastSelectedStatus(lastStatusForSelected ?? "");
  }, [selectedVisibility]);

  useEffect(() => {
    if (modalData?.id && modalData?.type) {
      const params = `detail/admin/${modalData?.type}/${modalData?.id}/`;
      dispatch(QuestionDetailsAction(params));
    }
  }, [modalData?.id, modalData?.type]);

  useEffect(() => {
    if (!QuestionDetailsState?.loading) {
      if (QuestionDetailsState?.status && QuestionDetailsState?.data) {
        setLoading(false);
        const data: FullQuestionType = QuestionDetailsState?.data;
        setData(data);
        setValue("question_id", data?.id);
        setValue("question_type", data?.question_type);
      } else {
        setData(null);
        setLoading(false);
      }
    } else {
      setLoading(true);
    }
  }, [QuestionDetailsState]);

  const {
    formState: { errors },
    setValue,
    handleSubmit,
    resetField,
  } = useForm<FormInputs>({
    resolver: yupResolver(formSchema),
  });

  useEffect(() => {
    if (
      !QuestionVerifyState?.loading &&
      QuestionVerifyState?.status &&
      QuestionVerifyState?.data
    ) {
      resetField("remark");
      setReviewText("");
    }
  }, [QuestionVerifyState]);

  const onSubmit = (payload: FormInputs) => {
    const params = "questions/validation/response/";
    dispatch(QuestionVerifyAction(params, payload));
  };

  return (
    <Modal
      open={modalData?.open && !loading}
      onClose={() => {
        setIsOpen(null);
      }}
      className="admin-panel-modal-container"
    >
      {data !== null ? (
        <div className="admin-panel-modal">
          <div className="admin-panel-modal-heading-container">
            <h4 className="admin-panel-modal-heading">Review Question</h4>
            <button
              type="button"
              onClick={() => {
                setIsOpen(null);
                setData(null);
              }}
              className="admin-panel-modal-close-btn"
            >
              <span className="mdi mdi-window-close"></span>
            </button>
          </div>
          <div className="admin-panel-modal-separator"></div>
          <div className="admin-panel-main-container">
            <div>
              <h5 className="d-flex align-items-center gap-2">
                {data?.title}
                {data?.is_verified && Icons?.verified}
              </h5>
            </div>
            <div className="d-flex align-items-center gap-2">
              <p
                className={`admin-panel-modal-chips ${
                  data?.difficulty?.toLowerCase() === "hard"
                    ? "admin-panel-modal-hard-difficulty"
                    : data?.difficulty?.toLowerCase() === "medium"
                    ? "admin-panel-modal-medium-difficulty"
                    : data?.difficulty?.toLowerCase() === "easy"
                    ? "admin-panel-modal-easy-difficulty"
                    : ""
                }`}
              >
                Difficulty: {toSentenceCase(data?.difficulty)}
              </p>
              {data?.coins > 0 && (
                <p className="admin-panel-modal-chips">Beans: {data?.coins}</p>
              )}
            </div>
            <div>
              <h6>Description</h6> {parse(data?.description ?? "")}
            </div>
            {data?.attachments?.length > 0 && (
              <div className="admin-panel-modal-description-attachment">
                <h6>Description Attachment</h6>
                {data?.attachments?.map((attachment: any) => (
                  <img
                    src={attachment?.attachment_file}
                    alt="Skillbrew.AI | Admin Panel | question attachment"
                    onError={(e) =>
                      (e.currentTarget.alt = "File Format not supported")
                    }
                  />
                ))}
              </div>
            )}
            {data?.language && (
              <div className="admin-panel-modal-tags-heading-container">
                <h6>{`Compatible Language${
                  data?.language?.length > 1 ? "s" : ""
                }`}</h6>
                <div className="admin-panel-modal-tags-wrapper">
                  {data?.language?.map((val: any) => (
                    <div className="admin-panel-modal-tag" key={val?.id}>
                      {val?.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data?.tags?.length > 0 && (
              <div className="admin-panel-modal-tags-heading-container">
                <h6>Tags</h6>
                <div className="admin-panel-modal-tags-wrapper">
                  {data?.tags?.map((tag: any) => (
                    <div className="admin-panel-modal-tag" key={tag?.id}>
                      {tag?.name}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data?.topics?.length > 0 && (
              <div className="admin-panel-modal-tags-heading-container">
                <h6>Topics</h6>
                {data?.topics?.map((topic: any) => (
                  <div className="admin-panel-modal-tag" key={topic?.id}>
                    {topic?.name}
                  </div>
                ))}
              </div>
            )}
            {data?.options?.length && (
              <div>
                <h5>Options</h5>
                <div className="admin-panel-modal-options-grid">
                  {data?.options?.map((option, index) => (
                    <div
                      key={option?.id}
                      className={
                        "admin-panel-modal-option-card-container " +
                        (option?.is_correct
                          ? "admin-panel-modal-option-card-correct"
                          : "admin-panel-modal-option-card-wrong")
                      }
                    >
                      <div className="admin-panel-modal-option-wrapper">
                        <p>{index + 1}.</p>
                        <p className="admin-panel-modal-option-card-text">
                          {option?.text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data?.input_format && (
              <div>
                <h6>Input Format</h6>
                <div>{parse(data?.input_format)}</div>
              </div>
            )}
            {data?.output_format && (
              <div>
                <h6>Output Format</h6>
                <div>{parse(data?.output_format)}</div>
              </div>
            )}
            {data?.testcases?.length && (
              <div>
                <h5>Test Cases</h5>
                {data?.testcases?.map((tc, index) => (
                  <div key={tc?.id} className="admin-panel-modal-testcase-card">
                    <div className="d-flex justify-content-between w-100">
                      <h6>Testcase #{index + 1}</h6>
                      <p className="d-flex gap-1 align-items-center">
                        <strong>Locked:</strong>
                        {tc?.is_locked ? (
                          <span className="mdi mdi-check-all"></span>
                        ) : (
                          <span className="mdi mdi-close"></span>
                        )}
                        <strong>Passed by Solution:</strong>
                        {tc?.pass_by_solution ? (
                          <span className="mdi mdi-check-all"></span>
                        ) : (
                          <span className="mdi mdi-close"></span>
                        )}
                      </p>
                    </div>
                    <p>
                      <strong>Input:</strong>
                      <code>
                        &nbsp;{tc?.input === "" ? "(empty)" : tc?.input}
                      </code>
                    </p>
                    <p>
                      <strong>Expected Output:</strong>
                      <code>&nbsp;{tc?.expected_output}</code>
                    </p>
                    <p>
                      <strong>Explanation:</strong>
                      {tc?.explanation === ""
                        ? "empty"
                        : parse(tc?.explanation ?? "")}
                    </p>
                  </div>
                ))}
              </div>
            )}
            {data?.solutions && data?.solutions?.length > 0 && (
              <SolutionsTabs solutions={data?.solutions} />
            )}
            <div className="admin-panel-modal-separator"></div>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="admin-panel-modal-verify-form-container"
            >
              <div className="admin-panel-modal-verify-btn-container">
                <div>
                  <div className="admin-panel-modal-label">
                    Visibility Level
                  </div>
                  <Select
                    value={selectedVisibility}
                    onChange={(e) => {
                      setSelectedVisibility(e?.target?.value);
                      setValue("visiblity_status", e?.target?.value, {
                        shouldValidate: true,
                      });
                    }}
                    displayEmpty
                    MenuProps={{
                      disablePortal: true,
                    }}
                    disabled={!visibilityOptions?.length}
                    size="small"
                    sx={{ width: "max-content" }}
                  >
                    <MenuItem value="" disabled>
                      Select Visibility
                    </MenuItem>
                    {visibilityOptions?.map((opt) => (
                      <MenuItem key={opt?.value} value={opt?.value}>
                        {capitalizeFirstLetter(opt?.value)}
                      </MenuItem>
                    ))}
                  </Select>
                  <ErrorMessage
                    errors={errors}
                    name="visiblity_status"
                    render={({ message }) => (
                      <p
                        style={{
                          color: "red",
                          textAlign: "left",
                        }}
                      >
                        {message}
                      </p>
                    )}
                  />
                </div>
                <div>
                  <div className="admin-panel-modal-label">Status</div>
                  <Select
                    value={selectedVerify}
                    onChange={(e) => {
                      setSelectedVerify(e?.target?.value);
                      setValue("status", e?.target?.value, {
                        shouldValidate: true,
                      });
                    }}
                    MenuProps={{
                      disablePortal: true,
                    }}
                    displayEmpty
                    size="small"
                    disabled={!visibilityOptions?.length || !selectedVisibility}
                  >
                    <MenuItem value="" disabled>
                      Select action
                    </MenuItem>
                    {VerifyOptions?.map((opt) => (
                      <MenuItem
                        key={opt.value}
                        value={opt.value}
                        disabled={opt?.value === lastSelectedStatus}
                      >
                        {opt?.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <ErrorMessage
                    errors={errors}
                    name="status"
                    render={({ message }) => (
                      <p style={{ color: "red", textAlign: "left" }}>
                        {message}
                      </p>
                    )}
                  />
                </div>
              </div>
              <div>
                <div className="admin-panel-modal-label">Remark</div>
                <textarea
                  value={reviewText}
                  placeholder="Type your remark here."
                  className="admin-panel-modal-input"
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    setReviewText(e?.target?.value);
                    setValue("remark", e?.target?.value, {
                      shouldValidate: true,
                    });
                  }}
                  rows={4}
                />
                <ErrorMessage
                  errors={errors}
                  name="remark"
                  render={({ message }) => (
                    <p style={{ color: "red", textAlign: "left" }}>{message}</p>
                  )}
                />
              </div>
              <div className="d-flex justify-content-end align-items-center w-100">
                <button
                  type="submit"
                  className="primary-btn admin-panel-modal-submit-btn"
                  disabled={QuestionVerifyState?.loading}
                >
                  Verify
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className="admin-panel-modal">
          <div className="admin-panel-modal-heading-container justify-content-end">
            <button
              type="button"
              onClick={() => setIsOpen(null)}
              className="admin-panel-modal-close-btn"
            >
              <span className="mdi mdi-window-close"></span>
            </button>
          </div>
          <EmptyData title="Nothing to show here." />
        </div>
      )}
    </Modal>
  );
};

export default QuestionVerificationModal;
